import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Modal,
  Form,
  Row,
  Col,
  Tag,
  Space,
  Dropdown,
  message,
  Statistic,
  Progress,
  Tooltip,
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  MoreOutlined,
  ShopOutlined,
  InboxOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import useStore from '../store/useStore';
import { locationsAPI } from '../services/api';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorDisplay from '../components/common/ErrorDisplay';
import FormField from '../components/forms/FormField';
import RightDrawer from '../components/common/RightDrawer';
import DetailView, { DetailSection, DetailField } from '../components/common/DetailView';
import LocationDetailView from '../components/locations/LocationDetailView';

const { Search } = Input;

const Locations = () => {
  const { locations, setLocations } = useStore();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [editingLocation, setEditingLocation] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [currentView, setCurrentView] = useState('list'); // 'list' | 'detail'
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [form] = Form.useForm();

  const fetchLocations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await locationsAPI.getAll();
      setLocations(response.data);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [setLocations]);

  const handleSearch = (value) => {
    setSearchText(value);
  };

  const handleAddLocation = () => {
    setEditingLocation(null);
    form.resetFields();
    setDrawerVisible(true);
  };

  const handleEditLocation = (location) => {
    setEditingLocation(location);
    form.setFieldsValue(location);
    setDrawerVisible(true);
  };

  const handleDeleteLocation = async (locationId) => {
    try {
      await locationsAPI.delete(locationId);
      message.success('Location deleted successfully');
      await fetchLocations();
    } catch (err) {
      message.error('Failed to delete location');
    }
  };

  const handleDrawerClose = () => {
    setDrawerVisible(false);
    setEditingLocation(null);
    form.resetFields();
  };

  const handleDrawerSave = async () => {
    try {
      setDrawerLoading(true);
      const values = await form.validateFields();
      
      if (editingLocation) {
        await locationsAPI.update(editingLocation.id, values);
        message.success('Location updated successfully');
      } else {
        await locationsAPI.create(values);
        message.success('Location created successfully');
      }
      
      await fetchLocations();
      handleDrawerClose();
    } catch (err) {
      message.error('Failed to save location');
    } finally {
      setDrawerLoading(false);
    }
  };

  const handleViewLocation = (location) => {
    console.log('handleViewLocation called with:', location);
    setSelectedLocation(location);
    setCurrentView('detail');
  };

  const handleBackToList = () => {
    setCurrentView('list');
    setSelectedLocation(null);
  };

  const getLocationStatus = (location) => {
    if (!location.active) {
      return { color: 'red', text: 'Inactive' };
    }
    if (location.capacity && location.currentStock >= location.capacity * 0.9) {
      return { color: 'orange', text: 'Near Full' };
    }
    return { color: 'green', text: 'Active' };
  };

  const getCapacityUtilization = (location) => {
    if (!location.capacity) return 0;
    return Math.round((location.currentStock / location.capacity) * 100);
  };

  const filteredLocations = locations.filter(location =>
    location.name?.toLowerCase().includes(searchText.toLowerCase()) ||
    location.code?.toLowerCase().includes(searchText.toLowerCase()) ||
    location.type?.toLowerCase().includes(searchText.toLowerCase())
  );

  const columns = [
    {
      title: 'Location',
      key: 'location',
      render: (_, record) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
            <ShopOutlined className="text-blue-600" />
          </div>
          <div>
            <div className="font-medium">{record.name}</div>
            <div className="text-sm text-gray-500">Code: {record.code}</div>
          </div>
        </div>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type) => (
        <Tag color="blue">{type}</Tag>
      ),
    },
    {
      title: 'Address',
      dataIndex: 'address',
      key: 'address',
      render: (address) => (
        <div className="max-w-xs">
          <div className="truncate">{address}</div>
        </div>
      ),
    },
    {
      title: 'Capacity',
      key: 'capacity',
      render: (_, record) => (
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-sm">{record.currentStock || 0} / {record.capacity || 'Unlimited'}</span>
            {record.capacity && (
              <span className="text-xs text-gray-500">
                {getCapacityUtilization(record)}%
              </span>
            )}
          </div>
          {record.capacity && (
            <Progress
              percent={getCapacityUtilization(record)}
              size="small"
              status={getCapacityUtilization(record) > 90 ? 'exception' : 'normal'}
              showInfo={false}
            />
          )}
        </div>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => {
        const status = getLocationStatus(record);
        return (
          <Tag color={status.color} icon={
            status.text === 'Active' ? <CheckCircleOutlined /> : 
            status.text === 'Near Full' ? <WarningOutlined /> : null
          }>
            {status.text}
          </Tag>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button 
            type="primary" 
            size="small" 
            icon={<EyeOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              handleViewLocation(record);
            }}
          >
            View
          </Button>
          <Dropdown
            menu={{
              items: [
                {
                  key: 'edit',
                  label: 'Edit Location',
                  icon: <EditOutlined />,
                  onClick: () => handleEditLocation(record),
                },
                {
                  key: 'delete',
                  label: 'Delete Location',
                  icon: <DeleteOutlined />,
                  danger: true,
                  onClick: () => handleDeleteLocation(record.id),
                },
              ],
            }}
            trigger={['click']}
          >
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        </Space>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
  };

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  if (loading) {
    return <LoadingSpinner size="large" tip="Loading locations..." />;
  }

  if (error) {
    return (
      <ErrorDisplay
        error={error}
        onRetry={fetchLocations}
      />
    );
  }

  // Render detail view if a location is selected
  if (currentView === 'detail' && selectedLocation) {
    return (
      <LocationDetailView
        location={selectedLocation}
        onBack={handleBackToList}
        onEdit={handleEditLocation}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-slate-100">Locations</h1>
          <p className="text-gray-600">Manage warehouse and storage locations</p>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAddLocation}
          className="w-full sm:w-auto"
        >
          Add Location
        </Button>
      </div>

      {/* Stats Cards */}
      {currentView === 'list' && (
      <Row gutter={16}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Locations"
              value={locations.length}
              prefix={<ShopOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Active Locations"
              value={locations.filter(l => l.active).length}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Capacity"
              value={locations.reduce((sum, l) => sum + (l.capacity || 0), 0)}
              prefix={<InboxOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Near Full"
              value={locations.filter(l => getCapacityUtilization(l) > 90).length}
              prefix={<WarningOutlined />}
            />
          </Card>
        </Col>
      </Row>
      )}

      {/* Search and Filters */}
      {currentView === 'list' && (
      <Card>
        <div className="flex flex-col sm:flex-row gap-4 mb-4">
          <Search
            placeholder="Search locations..."
            allowClear
            onSearch={handleSearch}
            onChange={(e) => handleSearch(e.target.value)}
            className="sm:w-80"
          />
        </div>

          <Table
            columns={columns}
            dataSource={filteredLocations}
            rowKey={(record) => record.id || record._id || record.name}
            onRow={(record) => ({
              onClick: (event) => {
                // Prevent row click if clicking on action buttons or interactive elements
                const target = event.target;
                if (target.closest('.ant-dropdown') || 
                    target.closest('.ant-dropdown-trigger') || 
                    target.closest('.ant-btn') ||
                    target.closest('button') ||
                    target.closest('[role="button"]') ||
                    target.closest('.ant-checkbox') ||
                    target.tagName === 'BUTTON') {
                  event.stopPropagation();
                  return;
                }
                console.log('Row clicked:', record);
                handleViewLocation(record);
              },
              style: { cursor: 'pointer' },
              className: 'hover:bg-gray-50'
            })}
            pagination={{
              total: filteredLocations.length,
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} locations`,
            }}
            scroll={{ x: 800 }}
          />
      </Card>
      )}

      {/* Right Drawer for Add/Edit */}
      <RightDrawer
        title={editingLocation ? 'Edit Location' : 'Add New Location'}
        open={drawerVisible}
        onClose={handleDrawerClose}
        loading={drawerLoading}
        width={640}
      >
        <Form
          form={form}
          layout="vertical"
          className="mt-2"
          onFinish={handleDrawerSave}
          id="drawer-form"
        >
          <Row gutter={16}>
            <Col span={12}>
              <FormField
                name="name"
                label="Location Name"
                rules={[{ required: true, message: 'Please enter location name' }]}
                placeholder="Enter location name"
              />
            </Col>
            <Col span={12}>
              <FormField
                name="code"
                label="Location Code"
                rules={[{ required: true, message: 'Please enter location code' }]}
                placeholder="Enter location code"
              />
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <FormField
                name="type"
                label="Location Type"
                type="select"
                options={[
                  { value: 'Warehouse', label: 'Warehouse' },
                  { value: 'Store', label: 'Store' },
                  { value: 'Distribution Center', label: 'Distribution Center' },
                  { value: 'Storage Room', label: 'Storage Room' },
                ]}
                rules={[{ required: true, message: 'Please select location type' }]}
                placeholder="Select location type"
              />
            </Col>
            <Col span={12}>
              <FormField
                name="capacity"
                label="Capacity"
                type="number"
                placeholder="Enter capacity (optional)"
              />
            </Col>
          </Row>

          <FormField
            name="address"
            label="Address"
            type="textarea"
            placeholder="Enter address"
            rows={3}
          />

          <Row gutter={16}>
            <Col span={12}>
              <FormField
                name="active"
                label="Status"
                type="switch"
                checkedChildren="Active"
                unCheckedChildren="Inactive"
              />
            </Col>
            <Col span={12}>
              <FormField
                name="notes"
                label="Notes"
                type="textarea"
                placeholder="Additional notes (optional)"
                rows={2}
              />
            </Col>
          </Row>
        </Form>
      </RightDrawer>
    </div>
  );
};

export default Locations;
